import posthog from "posthog-js";

import { fitLabelsToLabelStock } from "@/lib/lab-label";

import type {
  AccessCollectionInstructions,
  AccessLabPanel,
  AccessLabPanelCreate,
  AccessLabPanelUpdate,
  AccessLabTestCode,
  InHouseLabMenu,
  JunctionPayor,
  LabAccount,
  LabBiomarkerHistory,
  LabBiomarkerResult,
  LabInfo,
  LabOrder,
  LabOrderBatchCreate,
  LabOrderCreate,
  LabOrderFetchParams,
  LabOrderListItem,
  LabOrderTableItem,
  LabPanelCreate,
  LabPanelUpdate,
  LabTest,
  LastJunctionPayor,
  Marker,
  PSCLocation,
} from "@/types/lab";
import type { Page } from "@/types/pagination";

import { kyInstance, requestData } from "@/api/api-config";
import { labOrderFiltersToSearchParams } from "@/api/provider/lab/filter-search-params";

export async function fetchLabTests(params: {
  name?: string;
  labSlug?: string;
  collectionMethod?: string;
  cursor?: string;
}): Promise<LabTest[] | null> {
  const searchParams: Record<string, string> = {};
  if (params.name) searchParams.name = params.name;
  if (params.labSlug) searchParams.lab_slug = params.labSlug;
  if (params.collectionMethod)
    searchParams.collection_method = params.collectionMethod;
  if (params.cursor) searchParams.cursor = params.cursor;

  return await requestData<LabTest[]>("lab/test", "get", { searchParams });
}

export async function fetchMarkers(
  panelId: string,
  // Default swallows errors (returns null) for callers that tolerate it; pass
  // false so a failed load throws instead of masquerading as an empty panel.
  catchError: boolean = true,
): Promise<Marker[] | null> {
  return await requestData<Marker[]>(
    `lab/test/${panelId}/markers`,
    "get",
    undefined,
    catchError,
  );
}

export interface MarkersResponse {
  markers: Marker[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export async function searchMarkers(params: {
  name?: string;
  labId?: number;
  labSlug?: string;
  aLaCarteEnabled?: boolean;
  page?: number;
  size?: number;
}): Promise<MarkersResponse | null> {
  const searchParams: Record<string, string | number | boolean> = {};
  if (params.name) searchParams.name = params.name;
  if (params.labId) searchParams.lab_id = params.labId;
  if (params.labSlug) searchParams.lab_slug = params.labSlug;
  if (params.aLaCarteEnabled !== undefined)
    searchParams.a_la_carte_enabled = params.aLaCarteEnabled;
  if (params.page) searchParams.page = params.page;
  if (params.size) searchParams.size = params.size;

  return await requestData<MarkersResponse>("lab/markers", "get", {
    searchParams,
  });
}

// catchError = false on both: Junction's validation message ("marker 2075 is not
// available for this lab") is the whole point of the error path.
export async function createLabPanel(
  data: LabPanelCreate,
): Promise<LabTest | null> {
  return await requestData<LabTest>("lab/test", "post", { json: data }, false);
}

export async function updateLabPanel(
  panelId: string,
  data: LabPanelUpdate,
): Promise<LabTest | null> {
  return await requestData<LabTest>(
    `lab/test/${panelId}`,
    "patch",
    { json: data },
    false,
  );
}

export async function fetchAccessLabPanels(): Promise<AccessLabPanel[] | null> {
  return await requestData<AccessLabPanel[]>("lab/access/panels", "get");
}

// catchError = false, as for the Junction writes: the route names the unknown codes.
export async function createAccessLabPanel(
  data: AccessLabPanelCreate,
): Promise<AccessLabPanel | null> {
  return await requestData<AccessLabPanel>(
    "lab/access/panels",
    "post",
    { json: data },
    false,
  );
}

export async function updateAccessLabPanel(
  panelId: string,
  data: AccessLabPanelUpdate,
): Promise<AccessLabPanel | null> {
  return await requestData<AccessLabPanel>(
    `lab/access/panels/${panelId}`,
    "patch",
    { json: data },
    false,
  );
}

export async function fetchLabs(): Promise<LabInfo[] | null> {
  return await requestData<LabInfo[]>("lab/labs", "get");
}

/**
 * Search the Access (Comtron/LabGen) test compendium. Search-only, like ICD-10
 * search — a blank query short-circuits to `[]` rather than scanning Access's
 * whole client base (16k+ codes, including other practices' private panels).
 */
export async function searchAccessLabTests(params: {
  query: string;
  page: number;
  perPage: number;
}): Promise<Page<AccessLabTestCode> | null> {
  return await requestData<Page<AccessLabTestCode>>(
    "lab/access/tests/search",
    "get",
    { searchParams: params },
    false,
  );
}

export async function fetchAccessCollectionInstructions(
  orderId: string,
): Promise<AccessCollectionInstructions | null> {
  return await requestData<AccessCollectionInstructions>(
    `lab/access/order/${orderId}/collection-instructions`,
    "get",
    undefined,
    false,
  );
}

export async function fetchLabAccounts(): Promise<LabAccount[] | null> {
  return await requestData<LabAccount[]>("lab/lab-accounts", "get");
}

export async function fetchPSCLocations(params: {
  zipCode: string;
  labId: number;
  radius?: number;
}): Promise<PSCLocation[] | null> {
  const searchParams: Record<string, string | number> = {
    zip_code: params.zipCode,
    lab_id: params.labId,
  };
  if (params.radius) searchParams.radius = params.radius;

  return await requestData<PSCLocation[]>("lab/psc", "get", { searchParams });
}

export async function fetchAreaInfo(params: {
  zipCode: string;
  radius?: number;
}): Promise<unknown> {
  const searchParams: Record<string, string | number> = {
    zip_code: params.zipCode,
  };
  if (params.radius) searchParams.radius = params.radius;

  return await requestData("lab/area-info", "get", { searchParams });
}

export async function searchPayors(
  name: string,
): Promise<JunctionPayor[] | null> {
  return await requestData<JunctionPayor[]>("lab/insurance/payors", "get", {
    searchParams: { name },
  });
}

export async function fetchLastPayor(params: {
  patientId: string;
  insuranceCoverageId: string;
}): Promise<LastJunctionPayor | null> {
  return await requestData<LastJunctionPayor>(
    "lab/insurance/last-payor",
    "get",
    {
      searchParams: {
        patientId: params.patientId,
        insuranceCoverageId: params.insuranceCoverageId,
      },
    },
  );
}

export interface SyncJunctionUserResponse {
  labUserId: string;
}

export async function syncJunctionUser(
  patientId: string,
): Promise<SyncJunctionUserResponse | null> {
  return await requestData<SyncJunctionUserResponse>(
    `lab/patients/${patientId}/sync-junction-user`,
    "post",
    undefined,
    false,
  );
}

export async function createLabOrders(
  batch: LabOrderBatchCreate,
): Promise<LabOrder[] | null> {
  return await requestData<LabOrder[]>(
    "lab/order",
    "post",
    { json: batch },
    false,
  );
}

/**
 * Draft one or more Access orders. A separate endpoint from `createLabOrders`
 * because that one is declared on the LABS-gated Junction router — an
 * Access-only tenant (ACCESS_LABS without LABS) would 403 there and could
 * never reach Access's own submit route at all.
 */
export async function createAccessLabOrders(
  batch: LabOrderBatchCreate,
): Promise<LabOrder[] | null> {
  return await requestData<LabOrder[]>(
    "lab/access/order",
    "post",
    { json: batch },
    false,
  );
}

export async function submitDraftOrder(
  orderId: string,
  orderData: LabOrderCreate,
): Promise<LabOrder | null> {
  return await requestData<LabOrder>(
    `lab/order/${orderId}/submit`,
    "post",
    { json: orderData },
    false,
  );
}

/**
 * Submit a DRAFT/FAILED Access order: build and upload its CSV, then capture
 * the requisition Access hands back inline in that same response. Unlike
 * Junction's submit, this takes no body — everything the CSV needs was
 * already saved on the draft by `createLabOrders` (Access is create-then-
 * submit in one shot; there is no draft-edit step to PATCH first).
 */
/** Without an account, the backend picks the order location's account, else
 *  the clinic's all-locations one. */
export async function submitAccessOrder(
  orderId: string,
  accessLabAccountId?: string,
): Promise<LabOrder | null> {
  return await requestData<LabOrder>(
    `lab/access/order/${orderId}/submit`,
    "post",
    accessLabAccountId ? { json: { accessLabAccountId } } : undefined,
    false,
  );
}

/** What the clinic's own analyzer can run. 404 until Decoda sets the lab up. */
export async function fetchInHouseLabMenu(): Promise<InHouseLabMenu | null> {
  return await requestData<InHouseLabMenu>("lab/in-house/tests", "get");
}

/** Draft in-house orders, on the in-house lab's own gate. */
export async function createInHouseLabOrders(
  batch: LabOrderBatchCreate,
): Promise<LabOrder[] | null> {
  return await requestData<LabOrder[]>(
    "lab/in-house/order",
    "post",
    { json: batch },
    false,
  );
}

/**
 * Number the order and send it to the lab interface. The order that comes back
 * says how that went: "received" when the interface took it, or a failed order
 * with the interface's reason.
 */
export async function submitInHouseOrder(
  orderId: string,
): Promise<LabOrder | null> {
  return await requestData<LabOrder>(
    `lab/in-house/order/${orderId}/submit`,
    "post",
    undefined,
    false,
  );
}

/** Cancel at the lab interface, then in Decoda. Throws when the lab didn't get the cancel. */
export async function cancelInHouseOrder(
  orderId: string,
): Promise<LabOrder | null> {
  return await requestData<LabOrder>(
    `lab/in-house/order/${orderId}/cancel`,
    "post",
    undefined,
    false,
  );
}

export async function updateDraftLabOrder(
  orderId: string,
  orderData: LabOrderCreate,
): Promise<LabOrder | null> {
  return await requestData<LabOrder>(
    `lab/order/${orderId}/draft`,
    "patch",
    { json: orderData },
    false,
  );
}

export async function fetchLabOrders(
  patientId: string,
): Promise<LabOrderListItem[] | null> {
  return await requestData<LabOrderListItem[]>("lab/order", "get", {
    searchParams: { patient_id: patientId },
  });
}

export async function fetchLabBiomarkerHistory(
  patientId: string,
): Promise<LabBiomarkerHistory[] | null> {
  return await requestData<LabBiomarkerHistory[]>(
    `lab/patient/${patientId}/biomarkers`,
    "get",
  );
}

/** The clinic-wide `/labs` list — every order in the tenant, paginated. */
export async function fetchAllLabOrders(
  params?: LabOrderFetchParams,
  // Pass false so a failed load throws instead of reading as an empty page.
  catchError: boolean = true,
): Promise<Page<LabOrderTableItem> | null> {
  return await requestData<Page<LabOrderTableItem>>(
    "lab/orders",
    "get",
    { searchParams: labOrderFiltersToSearchParams(params) },
    catchError,
  );
}

export async function syncLabOrders(
  patientId: string,
): Promise<LabOrderListItem[] | null> {
  return await requestData<LabOrderListItem[]>("lab/order/sync", "post", {
    searchParams: { patient_id: patientId },
  });
}

export async function fetchLabOrder(orderId: string): Promise<LabOrder | null> {
  return await requestData<LabOrder>(`lab/order/${orderId}`, "get");
}

export interface LabOrderEmbedUrl {
  url: string;
}

/**
 * Mint a short-lived Junction App Embed URL for this order's locked dashboard
 * view. The URL expires, so callers should not cache it long — see the
 * `embedUrl` query option's staleTime/gcTime.
 */
export async function fetchLabOrderEmbedUrl(
  orderId: string,
): Promise<LabOrderEmbedUrl | null> {
  return await requestData<LabOrderEmbedUrl>(
    `lab/order/${orderId}/embed-url`,
    "get",
  );
}

export async function cancelLabOrder(
  orderId: string,
): Promise<LabOrder | null> {
  return await requestData<LabOrder>(`lab/order/${orderId}/cancel`, "post");
}

/** Stamp results as viewed (idempotent) — flips a "Results ready" order to "Completed". */
export async function markResultsViewed(
  orderId: string,
): Promise<LabOrder | null> {
  return await requestData<LabOrder>(
    `lab/order/${orderId}/mark-results-viewed`,
    "post",
  );
}

export async function fetchLabResults(
  orderId: string,
): Promise<LabBiomarkerResult[] | null> {
  return await requestData<LabBiomarkerResult[]>(
    `lab/order/${orderId}/result`,
    "get",
  );
}

export async function openRequisitionPdf(orderId: string): Promise<void> {
  const response = await kyInstance.get(`lab/order/${orderId}/requisition/pdf`);
  const blob = await response.blob();
  const url = window.URL.createObjectURL(
    new Blob([blob], { type: "application/pdf" }),
  );
  window.open(url, "_blank");
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

/**
 * Junction's collection-instructions PDF, or null while Junction is still
 * building it (about five minutes after the order is created). The 404 is read
 * here rather than thrown so a not-yet-ready order isn't reported as an error;
 * that also skips ky's PostHog hook, so other failures are reported here.
 */
export async function fetchCollectionInstructionsPdf(
  orderId: string,
): Promise<Blob | null> {
  const response = await kyInstance.get(
    `lab/order/${orderId}/collection-instructions/pdf`,
    { throwHttpErrors: false },
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      detail?: unknown;
    } | null;
    const error = new Error(
      typeof body?.detail === "string"
        ? body.detail
        : "The collection instructions PDF could not be loaded.",
    );
    posthog.captureException(error, {
      $request_url: response.url,
      $request_method: "GET",
      $response_status: response.status,
    });
    throw error;
  }
  return await response.blob();
}

export function openCollectionInstructionsPdf(blob: Blob): void {
  const url = window.URL.createObjectURL(
    new Blob([blob], { type: "application/pdf" }),
  );
  const opened = window.open(url, "_blank");
  if (!opened) {
    window.URL.revokeObjectURL(url);
    throw new Error(
      "The browser blocked the collection instructions window. Allow pop-ups for this site and try again.",
    );
  }
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

/**
 * Open Junction's specimen-label PDF for the browser's print dialog, resized
 * to the label stock the medication label prints on.
 *
 * `collectionDate` is the clock time the specimen was drawn — the lab compares
 * it against the received time at accessioning to decide whether the specimen
 * is still within each analyte's stability window — so callers pass the moment
 * of the draw, never a scheduled time.
 */
export async function openLabelsPdf(
  orderId: string,
  params: { collectionDate: string; numberOfLabels?: number },
): Promise<void> {
  const searchParams: Record<string, string> = {
    collection_date: params.collectionDate,
  };
  if (params.numberOfLabels !== undefined)
    searchParams.number_of_labels = String(params.numberOfLabels);

  const response = await kyInstance.get(`lab/order/${orderId}/labels/pdf`, {
    searchParams,
  });
  const vendorPdf = await response.arrayBuffer();
  let printable: BlobPart;
  try {
    printable = (await fitLabelsToLabelStock(
      vendorPdf,
    )) as Uint8Array<ArrayBuffer>;
  } catch (error) {
    // A vendor PDF we can't reopen still prints — just at its own page size,
    // which is what shipped before. Losing the labels entirely is worse.
    console.error("Failed to resize collection labels to label stock:", error);
    printable = vendorPdf;
  }
  const url = window.URL.createObjectURL(
    new Blob([printable], { type: "application/pdf" }),
  );
  const opened = window.open(url, "_blank");
  if (!opened) {
    // The browser only grants transient activation for a few seconds after the
    // click, and the vendor fetch alone is allowed 5s — so a slow print lands
    // here. Throwing routes it to the caller's toast; returning quietly would
    // clear the spinner with no tab and nothing said, which is the silent
    // no-op at the chair this flow exists to avoid.
    window.URL.revokeObjectURL(url);
    throw new Error(
      "The browser blocked the labels window. Allow pop-ups for this site and try again.",
    );
  }
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

export async function fetchLabResultsReadyCount(): Promise<number | null> {
  const result = await requestData<{ count: number }>(
    "lab/orders/results-ready-count",
    "get",
  );
  return result?.count ?? null;
}

export async function downloadResultPdf(orderId: string): Promise<void> {
  const response = await kyInstance.get(`lab/order/${orderId}/result/pdf`);
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `lab-results-${orderId}.pdf`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  a.remove();
}
